import React, { useEffect, useState } from 'react';
import { StyleSheet, View, Text, ScrollView, ActivityIndicator, TouchableOpacity, TextInput, Alert } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import { Ionicons } from '@expo/vector-icons';
import pilotReviewService from '../services/pilotReviewService';

const PilotProfileScreen = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const { user } = useSelector(state => state.auth);
  const { pilotId, missionId, pilotName } = route.params;
  
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(null);
  const [totalReviews, setTotalReviews] = useState(0);
  const [canReview, setCanReview] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Estado del formulario de reseña
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(false);

  useEffect(() => {
    loadPilotData();
  }, [pilotId, missionId]);

  const loadPilotData = async () => {
    try {
      setLoading(true);
      
      // Cargar reseñas del piloto
      const reviewsData = await pilotReviewService.getPilotReviews(pilotId);
      setReviews(reviewsData.reviews);
      setAverageRating(reviewsData.average_rating);
      setTotalReviews(reviewsData.total_reviews);
      
      // Verificar si puede dejar reseña (solo para usuarios enterprise)
      if (user?.is_enterprise_user && missionId) {
        const canReviewData = await pilotReviewService.canReview(pilotId, missionId);
        setCanReview(canReviewData.can_review);
        setExistingReview(canReviewData.existing_review || null);
      }
    } catch (error) {
      console.error('Error al cargar datos del piloto:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!rating || rating < 1 || rating > 5) {
      Alert.alert('Error', 'La calificación debe ser entre 1 y 5');
      return;
    }

    try {
      setSubmitting(true);
      await pilotReviewService.createReview({
        pilot: pilotId,
        mission: missionId,
        rating,
        comment: comment.trim()
      });
      
      Alert.alert('Éxito', 'Reseña enviada correctamente');
      setShowReviewForm(false);
      setComment('');
      setRating(5);
      loadPilotData(); // Recargar datos
    } catch (error) {
      Alert.alert('Error', error.message || 'No se pudo enviar la reseña');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, size = 20) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Ionicons
          key={i}
          name={i <= rating ? 'star' : 'star-outline'}
          size={size}
          color="#f39c12"
        />
      );
    }
    return <View style={styles.starsContainer}>{stars}</View>;
  };

  const renderRatingSelector = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <TouchableOpacity
          key={i}
          onPress={() => setRating(i)}
          style={styles.starButton}
        >
          <Ionicons
            name={i <= rating ? 'star' : 'star-outline'}
            size={30}
            color="#f39c12"
          />
        </TouchableOpacity>
      );
    }
    return <View style={styles.starsContainer}>{stars}</View>;
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#2c3e50" />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      {/* Header del piloto */}
      <View style={styles.header}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{pilotName?.charAt(0).toUpperCase() || 'P'}</Text>
        </View>
        <Text style={styles.pilotName}>{pilotName || 'Piloto'}</Text>
        
        {averageRating && (
          <View style={styles.ratingSection}>
            {renderStars(Math.round(averageRating))}
            <Text style={styles.ratingText}>
              {averageRating} ({totalReviews} reseña{totalReviews !== 1 ? 's' : ''})
            </Text>
          </View>
        )}
      </View>

      {/* Botón para dejar reseña o mensaje informativo */}
      {user?.is_enterprise_user && (
        <View style={styles.section}>
          {canReview && !existingReview ? (
            <TouchableOpacity
              style={styles.reviewButton}
              onPress={() => setShowReviewForm(!showReviewForm)}
            >
              <Ionicons name="star-outline" size={20} color="white" />
              <Text style={styles.reviewButtonText}>Dejar Reseña</Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.infoMessage}>
              <Ionicons name="information-circle-outline" size={20} color="#3498db" />
              <Text style={styles.infoMessageText}>
                {existingReview 
                  ? "Ya has dejado una reseña para este piloto en esta misión" 
                  : "Solo puedes dejar reseñas en misiones completadas de tu empresa"
                }
              </Text>
            </View>
          )}
        </View>
      )}

      {/* Reseña existente */}
      {existingReview && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Tu Reseña</Text>
          <View style={styles.reviewCard}>
            {renderStars(existingReview.rating)}
            <Text style={styles.reviewComment}>{existingReview.comment}</Text>
            <Text style={styles.reviewDate}>
              {new Date(existingReview.created_at).toLocaleDateString('es-ES')}
            </Text>
          </View>
        </View>
      )}

      {/* Formulario de reseña */}
      {showReviewForm && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Dejar Reseña</Text>
          <View style={styles.reviewForm}>
            <Text style={styles.formLabel}>Calificación:</Text>
            {renderRatingSelector()}
            
            <Text style={styles.formLabel}>Comentario (opcional):</Text>
            <TextInput
              style={styles.commentInput}
              value={comment}
              onChangeText={setComment}
              placeholder="Escribe tu comentario sobre el piloto..."
              multiline
              numberOfLines={4}
            />
            
            <View style={styles.formButtons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowReviewForm(false)}
              >
                <Text style={styles.cancelButtonText}>Cancelar</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.submitButton}
                onPress={handleSubmitReview}
                disabled={submitting}
              >
                {submitting ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <Text style={styles.submitButtonText}>Enviar</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Lista de reseñas */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Reseñas ({totalReviews})</Text>
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <View key={review.id} style={styles.reviewCard}>
              {renderStars(review.rating)}
              {review.comment && (
                <Text style={styles.reviewComment}>{review.comment}</Text>
              )}
              <Text style={styles.reviewAuthor}>
                Por: {review.reviewer_name} - {new Date(review.created_at).toLocaleDateString('es-ES')}
              </Text>
            </View>
          ))
        ) : (
          <Text style={styles.noReviews}>No hay reseñas disponibles</Text>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    backgroundColor: 'white',
    alignItems: 'center',
    padding: 30,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#2c3e50',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  avatarText: {
    color: 'white',
    fontSize: 28,
    fontWeight: 'bold',
  },
  pilotName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
  },
  ratingSection: {
    alignItems: 'center',
  },
  starsContainer: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  ratingText: {
    fontSize: 16,
    color: '#7f8c8d',
  },
  section: {
    backgroundColor: 'white',
    padding: 20,
    marginTop: 10,
    marginHorizontal: 10,
    borderRadius: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 15,
  },
  reviewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3498db',
    padding: 15,
    borderRadius: 8,
  },
  reviewButtonText: {
    color: 'white',
    fontWeight: 'bold',
    marginLeft: 10,
  },
  reviewForm: {
    marginTop: 10,
  },
  formLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
  },
  starButton: {
    padding: 5,
  },
  commentInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 15,
    fontSize: 16,
    textAlignVertical: 'top',
    marginBottom: 20,
  },
  formButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#95a5a6',
    padding: 15,
    borderRadius: 8,
    marginRight: 10,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: 'white',
    fontWeight: 'bold',
  },
  submitButton: {
    flex: 1,
    backgroundColor: '#2ecc71',
    padding: 15,
    borderRadius: 8,
    marginLeft: 10,
    alignItems: 'center',
  },
  submitButtonText: {
    color: 'white',
    fontWeight: 'bold',
  },
  reviewCard: {
    backgroundColor: '#f8f9fa',
    padding: 15,
    borderRadius: 8,
    marginBottom: 15,
  },
  reviewComment: {
    fontSize: 16,
    color: '#2c3e50',
    marginTop: 10,
    marginBottom: 10,
  },
  reviewAuthor: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  reviewDate: {
    fontSize: 12,
    color: '#95a5a6',
    marginTop: 5,
  },
  noReviews: {
    textAlign: 'center',
    color: '#7f8c8d',
    fontStyle: 'italic',
    padding: 20,
  },
  infoMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e3f2fd',
    padding: 15,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#3498db',
  },
  infoMessageText: {
    flex: 1,
    marginLeft: 10,
    color: '#2c3e50',
    fontSize: 14,
  },
});

export default PilotProfileScreen;